import { useEffect, useState } from "react"

import { useInfiniteQuery } from "@tanstack/react-query"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"
import { NotificationType } from "@pma/types"
import { getDictionaryItemWithoutContext, getMessagesByCurrentUser } from "@pma/utils/utils"
import { useAuth } from "@stores/auth-store"
import NotificationList from "./notification-list"
import useTranslator from "@hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { notificationTab } from "@pma/utils/constants"

export const NotificationTabs = () => {
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const [messageTypes, setMessageTypes] = useState<NotificationType[] | []>([])

  const formatNotificationLength = (n: number) => (n > 9 ? "9+" : n)

  const {
    data: allNotifications,
    error,
    fetchNextPage: fetchAllNextPage,
    hasNextPage: allNextPage,
    isLoading: isLoadingAll,
    refetch: refetchAll,
  } = useInfiniteQuery({
    queryKey: ["all-notifications", orgId],
    queryFn: ({ pageParam = 1 }) =>
      getMessagesByCurrentUser({
        type: "all",
        get_logs: false,
        paging_per_page: 20,
        paging_start_page: pageParam,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    enabled: !!orgId,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const {
    data: unreadNotifications,
    refetch: refetchUnread,
    isLoading: isLoadingUnread,
    fetchNextPage: fetchUnreadNextPage,
    hasNextPage: unreadNextPage,
  } = useInfiniteQuery({
    queryKey: ["unread-notifications", orgId],
    queryFn: ({ pageParam = 1 }) =>
      getMessagesByCurrentUser({
        type: "unread",
        paging_per_page: 20,
        paging_start_page: pageParam,
        get_logs: false,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    enabled: !!orgId,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const {
    data: mentionedNotifications,
    refetch: refetchMentioned,
    isLoading: isLoadingMentioned,
    fetchNextPage: fetchMentionedNextPage,
    hasNextPage: mentionedNextPage,
  } = useInfiniteQuery({
    queryKey: ["mentioned-notifications", orgId],
    queryFn: ({ pageParam = 1 }) =>
      getMessagesByCurrentUser({
        type: "mentioned",
        get_logs: false,
        paging_per_page: 20,
        paging_start_page: pageParam,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    enabled: !!orgId,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const {
    data: assignedNotifications,
    refetch: refetchAssigned,
    fetchNextPage: fetchAssignedNextPage,
    hasNextPage: assignedNextPage,
    isLoading: isLoadingAssigned,
  } = useInfiniteQuery({
    queryKey: ["assigned-notifications", orgId],
    queryFn: ({ pageParam = 1 }) =>
      getMessagesByCurrentUser({
        type: "assigned",
        get_logs: false,
        paging_per_page: 20,
        paging_start_page: pageParam,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    enabled: !!orgId,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const getMoreData = (context?: string) => {
    if (context === "all") {
      return allNextPage ? fetchAllNextPage() : false
    } else if (context === "mentioned") {
      return mentionedNextPage ? fetchMentionedNextPage() : false
    } else if (context === "assigned") {
      return assignedNextPage ? fetchAssignedNextPage() : false
    } else if (context === "unread") {
      return unreadNextPage ? fetchUnreadNextPage() : false
    } else {
      refetchAll()
      refetchMentioned(), refetchUnread(), refetchAssigned()
    }
  }

  const tabTitles: any = {
    all: `${translate("All")} (${formatNotificationLength(allNotifications?.pages[0].itemsTotal ?? 0)})`,
    unread:
      translate("Unread") +
      `(${formatNotificationLength(unreadNotifications?.pages[0].itemsTotal ?? 0)})`,
    mentioned: `${translate("I was mentioned")} (${formatNotificationLength(
      mentionedNotifications?.pages[0].itemsTotal ?? 0,
    )})`,
    assigned: `${translate("Assigned to me")} (${formatNotificationLength(
      assignedNotifications?.pages[0].itemsTotal ?? 0,
    )})`,
  }

  async function fetchMessageTypes() {
    const response = await getDictionaryItemWithoutContext("/dictionary/message_statuses")
    setMessageTypes(response)
  }

  useEffect(() => {
    fetchMessageTypes()
  }, [])

  return (
    <Tabs defaultValue='all' className={cn("w-full bg-none", isHebrew ? "rtl" : "ltr")}>
      <TabsList className='flex w-full grid-cols-4 justify-evenly rounded-full bg-slate-200 md:grid'>
        {notificationTab.map((tab: any, index: number) => (
          <TabsTrigger
            value={tab.value}
            key={index}
            className='rounded-full px-1 text-[8.8px] sm:px-2 sm:text-[10px] md:px-3 md:text-[9px] md:text-xs'
          >
            {tabTitles[tab.value]}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value='all'>
        <NotificationList
          data={allNotifications}
          onNotificationClick={refetchAll}
          loadMore={allNextPage ? getMoreData : undefined}
          context='all'
          isLoading={isLoadingAll}
        />
      </TabsContent>
      <TabsContent value='unread'>
        <NotificationList
          data={unreadNotifications}
          onNotificationClick={refetchUnread}
          loadMore={unreadNextPage ? getMoreData : undefined}
          context='unread'
          isLoading={isLoadingUnread}
        />
      </TabsContent>
      <TabsContent value='mentioned'>
        <NotificationList
          data={mentionedNotifications}
          onNotificationClick={refetchMentioned}
          loadMore={mentionedNextPage ? getMoreData : undefined}
          context='mentioned'
          isLoading={isLoadingMentioned}
        />
      </TabsContent>
      <TabsContent value='assigned'>
        <NotificationList
          data={assignedNotifications}
          onNotificationClick={refetchAssigned}
          loadMore={assignedNextPage ? getMoreData : undefined}
          context='assigned'
          isLoading={isLoadingAssigned}
        />
      </TabsContent>
    </Tabs>
  )
}
